import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  Globe,
  Smartphone,
  ShoppingCart,
  ArrowLeft,
} from "lucide-react";
import { canInstallPWA, isPWAInstalled } from "../utils/usePWAInstall";
import { useAuth } from "../auth/AuthContext";
import { TrackButton, TrackLink } from "./TrackLink";

export const InstallOrLogin = (props: { installApp: Function }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isPWAInstalled() || !canInstallPWA()) {
      if (user) {
        navigate("/dash", { replace: true });
      } else {
        navigate("/dash/login", { replace: true });
      }
    }
  }, [navigate, user]);

  const handleInstallClick = async () => {
    const outcome = await props.installApp();
    if (outcome === "accepted") {
      navigate("/dash/login");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="mb-8">
          <div className="flex justify-start mb-6">
            <TrackLink
              to="/"
              event="iol_back_home"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </TrackLink>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-600 rounded-xl shadow-lg">
                <ShoppingCart className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">MyMarket</h1>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 p-6">
            <div className="text-center text-white">
              <div className="mx-auto w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                <Smartphone className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Melhor experiência!</h2>
              <p className="text-blue-100">
                Instale o app para acessar suas listas offline e receber
                notificações
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Acesso offline</h3>
                  <p className="text-sm text-gray-600">
                    Use o app mesmo sem internet
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Mais rápido</h3>
                  <p className="text-sm text-gray-600">
                    Carregamento instantâneo
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Tela inicial</h3>
                  <p className="text-sm text-gray-600">
                    Acesso direto como um app nativo
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {canInstallPWA() ? (
                <TrackButton
                  event="iol_choice"
                  metadata={{ choice: "install_app" }}
                  onClick={handleInstallClick}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-5 w-5" />
                  Instalar aplicativo
                </TrackButton>
              ) : (
                <div className="text-center py-3">
                  <p className="text-sm text-gray-500 mb-2">
                    Para instalar o app, use o menu do seu navegador
                  </p>
                  <p className="text-xs text-gray-400 mb-4">
                    Procure por "Adicionar à tela inicial" ou "Instalar app"
                  </p>
                </div>
              )}

              <TrackLink
                event="iol_choice"
                metadata={{ choice: "web_version" }}
                to="/dash/login"
                className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                <Globe className="h-5 w-5" />
                Continuar na versão web
              </TrackLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
